/*
 * Barra promocional do topo.
 *
 * Custom element autocontido (sem Swiper) porque a barra fica acima da dobra e
 * o bundle do Swiper é carregado com `defer` — depender dele atrasava o
 * primeiro render e deixava os anúncios empilhados.
 *
 * Responsabilidades:
 *  - rotacionar os anúncios (autoplay + setas)
 *  - manter os contadores regressivos de cada anúncio
 *  - copiar o cupom ao clicar
 */
class AnnouncementPromoBar extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-promo-track]');
    this.slides = Array.from(this.querySelectorAll('[data-promo-slide]'));
    this.index = 0;
    this.timers = [];

    this.initCarousel();
    this.initCountdowns();
    this.initCoupons();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.timers.forEach((id) => clearInterval(id));
    this.timers = [];
  }

  /* ------------------------------------------------------------ Carrossel */

  initCarousel() {
    if (!this.track || this.slides.length === 0) return;

    this.track.setAttribute('data-ready', '');

    if (this.slides.length < 2) {
      this.querySelectorAll('[data-promo-arrow]').forEach((arrow) => {
        arrow.hidden = true;
      });
      return;
    }

    this.querySelectorAll('[data-promo-arrow]').forEach((arrow) => {
      arrow.addEventListener('click', () => {
        this.goTo(this.index + (arrow.dataset.promoArrow === 'next' ? 1 : -1));
        this.restartAutoplay();
      });
    });

    this.addEventListener('mouseenter', () => this.stopAutoplay());
    this.addEventListener('mouseleave', () => this.startAutoplay());
    this.addEventListener('focusin', () => this.stopAutoplay());
    this.addEventListener('focusout', () => this.startAutoplay());

    this.render();
    this.startAutoplay();
  }

  goTo(index) {
    const total = this.slides.length;
    this.index = ((index % total) + total) % total;
    this.render();
  }

  render() {
    if (!this.track) return;
    this.track.style.transform = `translateX(-${this.index * 100}%)`;
    this.slides.forEach((slide, i) => {
      const current = i === this.index;
      slide.setAttribute('aria-hidden', current ? 'false' : 'true');
      // Evita que links/cupons dos slides fora de tela recebam foco por Tab.
      slide.querySelectorAll('a, button').forEach((el) => {
        el.tabIndex = current ? 0 : -1;
      });
    });
  }

  startAutoplay() {
    if (this.autoplayId || this.dataset.autoplay !== 'true' || this.slides.length < 2) return;
    const speed = parseInt(this.dataset.speed, 10) || 5;
    this.autoplayId = setInterval(() => this.goTo(this.index + 1), speed * 1000);
  }

  stopAutoplay() {
    if (!this.autoplayId) return;
    clearInterval(this.autoplayId);
    this.autoplayId = null;
  }

  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  /* ------------------------------------------------------------ Contador */

  initCountdowns() {
    this.querySelectorAll('[data-promo-countdown]').forEach((el) => {
      const endDate = new Date(el.dataset.countdownEnd || '');
      if (Number.isNaN(endDate.getTime())) {
        el.hidden = true;
        return;
      }

      const units = {
        days: el.querySelector('[data-unit="days"]'),
        hours: el.querySelector('[data-unit="hours"]'),
        minutes: el.querySelector('[data-unit="minutes"]'),
        seconds: el.querySelector('[data-unit="seconds"]'),
      };
      const pad = (n) => String(n).padStart(2, '0');
      const hideOnEnd = el.dataset.countdownHideOnEnd === 'true';
      let intervalId = null;
      let finished = false;

      const tick = () => {
        const diff = Math.max(0, endDate.getTime() - Date.now());
        const totalSeconds = Math.floor(diff / 1000);

        // Sem tile de dias, as horas acumulam o total (ex.: 52 HRS).
        const days = units.days ? Math.floor(totalSeconds / 86400) : 0;
        const hours = Math.floor(totalSeconds / 3600) - days * 24;

        if (units.days) units.days.textContent = pad(days);
        if (units.hours) units.hours.textContent = pad(hours);
        if (units.minutes) units.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        if (units.seconds) units.seconds.textContent = pad(totalSeconds % 60);

        if (diff === 0) {
          finished = true;
          if (intervalId) clearInterval(intervalId);
          if (hideOnEnd) {
            const slide = el.closest('[data-promo-slide]') || el;
            slide.hidden = true;
          }
        }
      };

      tick();
      if (!finished) {
        intervalId = setInterval(tick, 1000);
        this.timers.push(intervalId);
      }
    });
  }

  /* --------------------------------------------------------------- Cupom */

  initCoupons() {
    this.querySelectorAll('[data-promo-coupon]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.copyCoupon(button);
      });
    });
  }

  async copyCoupon(button) {
    const code = (button.dataset.promoCoupon || '').trim();
    if (!code) return;

    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        copied = true;
      }
    } catch (error) {
      copied = false;
    }

    if (!copied) copied = AnnouncementPromoBar.legacyCopy(code);

    const feedback = button.querySelector('[data-promo-coupon-feedback]');
    if (feedback) {
      feedback.textContent = copied
        ? feedback.dataset.successText || 'Cupom copiado!'
        : feedback.dataset.errorText || 'Não foi possível copiar';
    }

    button.classList.add('is-copied');
    clearTimeout(this.couponFeedbackId);
    this.couponFeedbackId = setTimeout(() => button.classList.remove('is-copied'), 2000);
  }

  // Safari < 13.4 e contextos sem HTTPS não expõem a Clipboard API.
  static legacyCopy(code) {
    try {
      const input = document.createElement('textarea');
      input.value = code;
      input.setAttribute('readonly', '');
      input.style.position = 'fixed';
      input.style.left = '-9999px';
      document.body.appendChild(input);
      input.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(input);
      return ok;
    } catch (error) {
      return false;
    }
  }
}

if (!customElements.get('announcement-promo-bar')) {
  customElements.define('announcement-promo-bar', AnnouncementPromoBar);
}
